import { useState, useEffect } from "react";
import { Modal } from "../../shared-components/Modal";
import Input from "../../shared-components/Input";
import Button, { ButtonColorTypes } from "../../shared-components/Button";
import { useCompaniesFetchAll } from "../../api/CompaniesApi";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import DropdownCombo, {
  DropdownItem,
} from "../../shared-components/DropdownCombo";
import { useCreateLegacyUser } from "../../api/legacy/LegacyUserAPI";
import { useApplicationToast } from "../../webcontent/ApplicationToastProvider";

interface LegacyUserCreateProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: (data: {
    companyId: string;
    username: string;
    email: string;
  }) => void;
}

function LegacyUserCreate({
  isOpen,
  onClose,
  onCreate,
}: LegacyUserCreateProps) {
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [form, setForm] = useState({ companyId: "", username: "", email: "" });
  const {
    mutate: getAllCompanies,
    data: companies,
    isLoading,
    error,
  } = useCompaniesFetchAll();
  const { mutateAsync: createLegacyUser, isLoading: isCreating } =
    useCreateLegacyUser();
  const { showLargeToast } = useApplicationToast();

  useEffect(() => {
    if (isOpen) {
      setForm({ companyId: "", username: "", email: "" });
      setStep("form");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) getAllCompanies();
  }, [isOpen, getAllCompanies]);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStep("confirm");
  }

  async function handleConfirm() {
    try {
      await createLegacyUser({
        ossa_company_uuid: form.companyId,
        username: form.username,
        email: form.email,
      });
      showLargeToast?.({
        title: "Success",
        message: "User successfully created",
        timeout: 5000,
      });
      onCreate?.({
        companyId: form.companyId,
        username: form.username,
        email: form.email,
      });
      onClose();
    } catch (err: any) {
      let detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail = detail.map((d: any) => d.msg).join(", ");
      } else if (typeof detail === "object") {
        detail = JSON.stringify(detail);
      }
      showLargeToast?.({
        title: "User Creation Failed",
        message: detail || err?.message || "Error creating user",
        timeout: 5000,
      });
    }
  }

  function handleCancel() {
    onClose();
  }

  const companyItems: DropdownItem[] = (
    Array.from(
      new Map(
        (companies ?? []).map((c: { external_id: string; name: string }) => [
          c.external_id,
          c,
        ])
      ).values()
    ) as { external_id: string; name: string }[]
  ).map((company) => ({
    id: company.external_id,
    label: company.name,
  }));

  function handleCompanyChange(item: DropdownItem) {
    setForm((f) => {
      const prefillUsername = f.username
        ? f.username
        : item.label
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, "")
            .replace(/ /g, "") + "-admin";
      return { ...f, companyId: item.id, username: prefillUsername };
    });
  }

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} height="h-[700px]">
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="p-8 text-red-600">Error loading companies</div>
      ) : step === "form" ? (
        <form
          onSubmit={handleFormSubmit}
          className="flex flex-col justify-between gap-4 p-4 min-h-[400px] h-[400px]"
        >
          <div>
            <h2 className="text-lg font-semibold mb-6">Create Legacy User</h2>
            <label htmlFor="companyId" className="mb-2 block">
              Company
            </label>
            <DropdownCombo
              items={companyItems}
              name="companyId"
              label={undefined}
              placeHolder="Select a company..."
              needTypeAhead={true}
              onChange={handleCompanyChange}
              initial={
                form.companyId
                  ? companyItems.find((c) => c.id === form.companyId)
                  : undefined
              }
              containerClassName="mb-4"
            />
            {form.companyId && (
              <>
                <label htmlFor="username" className="mb-2 block">
                  Username
                </label>
                <Input
                  id="username"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  required
                  className="mb-4 w-3/4"
                />
                <label htmlFor="email" className="mb-2 block">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  required
                  className="mb-4 w-3/4"
                />
              </>
            )}
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              text="Cancel"
              onClick={handleCancel}
              className="hover:bg-gray-100"
              colorType={ButtonColorTypes.cancel}
            />
            <Button
              text="Next"
              className="hover:bg-green-500"
              colorType={ButtonColorTypes.primary}
            />
            <button type="submit" style={{ display: "none" }} aria-hidden />
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <h2 className="text-lg font-semibold mb-2">Confirm User Creation</h2>
          <div className="mb-2">
            Are you sure you want to create a legacy user with the following
            information?
          </div>
          <div className="bg-gray-50 rounded p-3 text-sm">
            <div>
              <span className="font-semibold">Company Name:</span>{" "}
              {companyItems.find((c) => c.id === form.companyId)?.label || ""}
            </div>
            <div>
              <span className="font-semibold">Company ID:</span>{" "}
              {form.companyId}
            </div>
            <div>
              <span className="font-semibold">Username:</span> {form.username}
            </div>
            <div>
              <span className="font-semibold">Email:</span> {form.email}
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              text="Back"
              onClick={handleCancel}
              className="hover:bg-gray-100"
              colorType={ButtonColorTypes.cancel}
            />
            <Button
              text={isCreating ? "Creating..." : "Submit"}
              onClick={handleConfirm}
              disabled={isCreating}
              className="hover:bg-green-500"
              colorType={ButtonColorTypes.primary}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default LegacyUserCreate;
